import React, { useEffect, useState } from 'react'; 
import {    
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Chip,
  Paper,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PropTypes from 'prop-types';
import { styled, keyframes } from '@mui/system';
import {
  createOrganization,
  getOrganizationById,
  updateOrganization,
  updateOrganizationTrialConfig,
  extendOrganizationTrial,
} from '../apis/Admin';
import { toast } from 'react-toastify';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const underlineGrow = keyframes`
  from { transform: scaleX(0); opacity: .2; }
  to   { transform: scaleX(1); opacity: 1; }
`;

const softPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(27,38,64,0.24); }
  70%  { box-shadow: 0 0 0 8px rgba(27,38,64,0.04); }
  100% { box-shadow: 0 0 0 0 rgba(27,38,64,0.0); }
`;

const TitleWrap = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  animation: `${fadeUp} 260ms ease-out both`,
  color: '#1B2640',
}));

const IconBadge = styled('div')(() => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background:
    'linear-gradient(180deg, rgba(27,38,64,0.12), rgba(27,38,64,0.06))',
  border: `1px solid #1B2640`,
  animation: `${softPulse} 2.2s ease-out infinite`,
}));

const TitleText = styled('div')(() => ({
  fontSize: '1.05rem',
  fontWeight: 700,
  color: '#000',
  letterSpacing: 0.2,
  lineHeight: 1.2,
}));

const TitleUnderline = styled('div')(() => ({
  height: 5,
  width: '100%',
  transformOrigin: 'left',
  transform: 'scaleX(1)',
  marginTop: 6,
  background: `linear-gradient(90deg, #1B2640, transparent)`,
  animation: `${underlineGrow} 480ms 120ms ease-out both`,
  opacity: 0.85,
}));

const StyledTextField = styled(TextField)(({ multiline }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    height: multiline ? 85 : 45,
    alignItems: 'flex-start',
    '& fieldset': { border: '0.5px solid #e5e7eb' },
    '&:hover fieldset': { border: '0.5px solid #d1d5db' },
    '&.Mui-focused fieldset': { border: '0.6px solid #1B2640' },
  },
  '& .MuiInputBase-input': {
    padding: multiline ? '8px 10px' : '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
  },
  '& .MuiInputLabel-root': {
    color: '#1B2640',
    fontSize: '0.875rem',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#1B2640' },
  '& .MuiInputLabel-root.Mui-error': { color: '#d32f2f' },
}));

const generateOrgId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback: manual UUID v4-style generator
  const s4 = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);

  return (
    s4() + s4() + '-' +
    s4() + '-' +
    s4() + '-' +
    s4() + '-' +
    s4() + s4() + s4()
  );
};

const empty = {
  org_id: '',
  org_name: '',
  description: '',
  total_seats: 10,
  is_active: true,
  ip_whitelist_enabled: false,
  allowed_ip_ranges: [],
  is_paid: false,
  subscription_period_months: null,
  subscription_period_days: null,
  trial_days: 15,
  max_connectors: 5,
  days_limit_enabled: true,
  connectors_limit_enabled: true,
  trial_start_date: null,
  trial_end_date: null,
};

export default function OrganizationDialog({
  open,
  mode = 'create',
  organization,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [usedSeats, setUsedSeats] = useState(0);
  const [newIpRange, setNewIpRange] = useState('');
  const [ipError, setIpError] = useState('');
  const [trialExtensionDays, setTrialExtensionDays] = useState('');
  const [extendingTrial, setExtendingTrial] = useState(false);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [subscriptionRemaining, setSubscriptionRemaining] = useState(null);
  const [originalValues, setOriginalValues] = useState(null);
  const [originalTrialConfig, setOriginalTrialConfig] = useState(null);
  const [savingTrialConfig, setSavingTrialConfig] = useState(false);

  const autoFormatIpAddress = (input) => {
    if (!input || !input.trim()) {
      return input;
    }

    const trimmed = input.trim();
    
    if (trimmed.includes('.')) {
      return trimmed;
    }

    const cidrMatch = trimmed.match(/^(\d+)(\/\d+)?$/);
    if (!cidrMatch) {
      return trimmed;
    }

    const digitsOnly = cidrMatch[1];
    const cidrPart = cidrMatch[2] || '';

    if (digitsOnly.length < 4) {
      return trimmed;
    }

    let remaining = digitsOnly;
    const octets = [];
    
    if (digitsOnly.length === 12) {
      for (let i = 0; i < 4; i++) {
        octets.push(digitsOnly.substring(i * 3, (i + 1) * 3));
      }
    } else {
      for (let i = 0; i < 4; i++) {
        if (remaining.length === 0) {
          octets.push('0');
        } else if (i === 3) {
          const lastOctet = remaining.length > 3 ? remaining.substring(0, 3) : remaining;
          octets.push(lastOctet);
          remaining = '';
        } else {
          const digitsNeeded = 4 - i - 1;
          const available = remaining.length - digitsNeeded;
          
          if (available <= 0) {
            octets.push('0');
          } else {
            let take = Math.min(3, available);
            
            if (take === 3) {
              const threeDigits = parseInt(remaining.substring(0, 3), 10);
              if (threeDigits > 255) {
                take = 2;
                const twoDigits = parseInt(remaining.substring(0, 2), 10);
                if (twoDigits > 255) {
                  take = 1;
                }
              }
            }
            
            octets.push(remaining.substring(0, take));
            remaining = remaining.substring(take);
          }
        }
      }
    }

    const formatted = octets.join('.');
    return formatted + cidrPart;
  };

  const validateIpRange = (ipRange) => {
    if (!ipRange || !ipRange.trim()) {
      return { valid: false, error: 'IP range cannot be empty', formatted: '' };
    }

    const formatted = autoFormatIpAddress(ipRange.trim());
    const trimmed = formatted.trim();
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    
    if (!ipv4Regex.test(trimmed)) {
      return { valid: false, error: 'Invalid IP format. Use format: 192.168.1.1 or 192.168.1.0/24', formatted };
    }

    if (trimmed.includes('/')) {
      const [, cidr] = trimmed.split('/');
      const cidrNum = parseInt(cidr, 10);
      if (isNaN(cidrNum) || cidrNum < 0 || cidrNum > 32) {
        return { valid: false, error: 'CIDR must be between 0 and 32', formatted };
      }
    }

    const parts = trimmed.split('/')[0].split('.');
    for (const part of parts) {
      const num = parseInt(part, 10);
      if (isNaN(num) || num < 0 || num > 255) {
        return { valid: false, error: 'Each IP octet must be between 0 and 255', formatted };
      }
    }

    return { valid: true, error: '', formatted };
  };

    useEffect(() => {
    if (!open) {
      setForm(empty);
      setErrors({});
      setUsedSeats(0);
      setTrialExtensionDays('');
      setSubscriptionEndDate(null);
      setSubscriptionRemaining(null);
      setOriginalValues(null);
      setOriginalTrialConfig(null);
      return;
    }

    if (mode === 'edit' && organization) {
      const loadOrg = async () => {
        setLoading(true);
        try {
          const fresh = await getOrganizationById(organization.org_id);
          
          // Calculate remaining subscription time if paid
          let remainingText = null;
          if (fresh.is_paid && fresh.subscription_end_date) {
            const endDate = new Date(fresh.subscription_end_date);
            const now = new Date();
            const diffTime = endDate - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            if (diffDays > 0) {
              if (diffDays >= 30) {
                const months = Math.floor(diffDays / 30);
                const days = diffDays % 30;
                if (days === 0) {
                  remainingText = `${months} month${months > 1 ? 's' : ''} left`;
                } else {
                  remainingText = `${months} month${months > 1 ? 's' : ''} ${days} day${days > 1 ? 's' : ''} left`;
                }
              } else {
                remainingText = `${diffDays} day${diffDays > 1 ? 's' : ''} left`;
              }
            } else {
              remainingText = 'Expired';
            }
          }
          
          const formData = {
            org_id: fresh.org_id || '',
            org_name: fresh.org_name || '',
            description: fresh.description || '',
            total_seats: fresh.total_seats || 0,
            is_active: fresh.is_active !== false,
            ip_whitelist_enabled: fresh.ip_whitelist_enabled || false,
            allowed_ip_ranges: fresh.allowed_ip_ranges || [],
            is_paid: fresh.is_paid || false,
            subscription_period_months: null,
            subscription_period_days: null,
            trial_days: fresh.trial_days || 15,
            max_connectors: fresh.max_connectors || 5,
            days_limit_enabled: fresh.days_limit_enabled !== false,
            connectors_limit_enabled: fresh.connectors_limit_enabled !== false,
            trial_start_date: fresh.trial_start_date || null,
            trial_end_date: fresh.trial_end_date || null,
          };
          setForm(formData);
          setOriginalValues({
            org_name: fresh.org_name || '',
            description: fresh.description || '',
            total_seats: fresh.total_seats || 0,
            is_active: fresh.is_active !== false,
            ip_whitelist_enabled: fresh.ip_whitelist_enabled || false,
            allowed_ip_ranges: fresh.allowed_ip_ranges || [],
            is_paid: fresh.is_paid || false,
          });
          setOriginalTrialConfig({
            trial_days: fresh.trial_days || 15,
            max_connectors: fresh.max_connectors || 5,
            days_limit_enabled: fresh.days_limit_enabled !== false,
            connectors_limit_enabled: fresh.connectors_limit_enabled !== false,
          });
          setSubscriptionEndDate(fresh.subscription_end_date || null);
          setSubscriptionRemaining(remainingText);
          setUsedSeats(fresh.used_seats || 0);
        } catch (err) {
          console.error('Failed to load organization:', err);
          toast.error(
            err?.response?.data?.detail ||
              err?.response?.data?.message ||
              err?.message ||
              'Failed to load organization',
          );
          onClose();
        } finally {
          setLoading(false);
        }
      };
      loadOrg();
    } else {
      // 🔹 Create mode: auto-generate org_id
      setForm({
        ...empty,
        org_id: generateOrgId(),
      });
      setUsedSeats(0);
    }
  }, [open, mode, organization?.org_id, onClose]);


  const validate = () => {
    const e = {};

    if (mode === 'create') {
      if (!form.org_id?.trim()) {
        e.org_id = 'Organization ID is required';
      } else if (form.org_id.trim().length > 64) {
        e.org_id = 'Organization ID must be 64 characters or less';
      }
    }

    if (!form.org_name?.trim()) {
      e.org_name = 'Organization Name is required';
    } else if (form.org_name.trim().length > 100) {
      e.org_name = 'Organization Name must be 100 characters or less';
    }

    if (form.description && form.description.trim().length > 200) {
      e.description = 'Description must be 200 characters or less';
    }

    if (mode === 'create' && form.total_seats === undefined) {
      e.total_seats = 'Total Seats is required';
    } else if (form.total_seats < 0) {
      e.total_seats = 'Total Seats cannot be negative';
    }

    if (mode === 'edit') {
      const totalSeatsNum = Number(form.total_seats);
      const usedSeatsNum = Number(usedSeats);
      if (!Number.isNaN(totalSeatsNum) && !Number.isNaN(usedSeatsNum)) {
        if (totalSeatsNum > 0 && totalSeatsNum < usedSeatsNum) {
          e.total_seats = `Cannot reduce total seats below current used seats (${usedSeats})`;
        }
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const hasChangesForUpdate = () => {
    if (mode === 'create' || !originalValues) {
      return true; 
    }

    const currentOrgName = form.org_name?.trim() || '';
    const currentDescription = form.description?.trim() || null;
    const currentTotalSeats = Number(form.total_seats) || 0;
    const currentIsActive = form.is_active;
    const currentIpWhitelistEnabled = form.ip_whitelist_enabled || false;
    const currentAllowedIpRanges = form.ip_whitelist_enabled && form.allowed_ip_ranges && form.allowed_ip_ranges.length > 0 
      ? form.allowed_ip_ranges 
      : null;
    
    const originalDescription = originalValues.description?.trim() || null;
    const originalAllowedIpRanges = originalValues.ip_whitelist_enabled && originalValues.allowed_ip_ranges && originalValues.allowed_ip_ranges.length > 0
      ? originalValues.allowed_ip_ranges
      : null;

    if (currentOrgName !== originalValues.org_name) return true;
    if (currentDescription !== originalDescription) return true;
    if (currentTotalSeats !== originalValues.total_seats) return true;
    if (currentIsActive !== originalValues.is_active) return true;
    if (currentIpWhitelistEnabled !== originalValues.ip_whitelist_enabled) return true;
    
    const currentRangesSorted = currentAllowedIpRanges ? [...currentAllowedIpRanges].sort() : null;
    const originalRangesSorted = originalAllowedIpRanges ? [...originalAllowedIpRanges].sort() : null;
    if (JSON.stringify(currentRangesSorted) !== JSON.stringify(originalRangesSorted)) return true;

    const isExpired = !form.is_paid && !form.is_active;
    if (form.is_paid !== undefined || isExpired) {
      if (form.is_paid !== originalValues.is_paid) return true;
      if (form.is_active !== originalValues.is_active) return true;
    }

    return false;
  };

  const hasTrialConfigChanges = () => {
    if (mode === 'create' || !originalTrialConfig) {
      return false;
    }

    if (form.trial_days !== originalTrialConfig.trial_days) return true;
    if (form.max_connectors !== originalTrialConfig.max_connectors) return true;
    if (form.days_limit_enabled !== originalTrialConfig.days_limit_enabled) return true;
    if (form.connectors_limit_enabled !== originalTrialConfig.connectors_limit_enabled) return true;

    return false;
  };

  const handleSaveTrialConfig = async () => {
    if (!hasTrialConfigChanges()) return;

    setSavingTrialConfig(true);
    try {
      const orgId = organization?.org_id;
      if (!orgId) {
        toast.error('Organization ID is missing');
        return;
      }

      await updateOrganizationTrialConfig(orgId, {
        trial_days: form.trial_days,
        max_connectors: form.max_connectors,
        days_limit_enabled: form.days_limit_enabled,
        connectors_limit_enabled: form.connectors_limit_enabled,
      });

      setOriginalTrialConfig({
        trial_days: form.trial_days,
        max_connectors: form.max_connectors,
        days_limit_enabled: form.days_limit_enabled,
        connectors_limit_enabled: form.connectors_limit_enabled,
      });

      toast.success('Trial settings saved successfully!');
      
      const fresh = await getOrganizationById(orgId);
      onSubmit(fresh);
    } catch (err) {
      console.error('Failed to save trial config:', err);
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          'Failed to save trial settings'
      );
    } finally {
      setSavingTrialConfig(false);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        org_name: form.org_name.trim(),
        description: form.description?.trim() || null,
        total_seats: Number(form.total_seats) || 0,
        is_active: form.is_active,
        ip_whitelist_enabled: form.ip_whitelist_enabled || false,
        allowed_ip_ranges: form.ip_whitelist_enabled && form.allowed_ip_ranges && form.allowed_ip_ranges.length > 0 
          ? form.allowed_ip_ranges 
          : null,
      };

      if (mode === 'create') {
        payload.org_id = form.org_id.trim();
        payload.is_paid = form.is_paid;
        if (form.is_paid) {
          payload.subscription_period_months = form.subscription_period_months;
          payload.subscription_period_days = form.subscription_period_days;
        }
        const res = await createOrganization(payload);
        
        if (!form.is_paid) {
          try {
            await updateOrganizationTrialConfig(res.org_id, {
              trial_days: form.trial_days,
              max_connectors: form.max_connectors,
              days_limit_enabled: form.days_limit_enabled,
              connectors_limit_enabled: form.connectors_limit_enabled,
            });
          } catch (trialErr) {
            console.error('Failed to set trial config:', trialErr);
            toast.warning('Organization created but trial config failed to save');
          }
        }
        
        toast.success('Organization created successfully!');
        onSubmit(res);
      } else {
        const orgId = organization?.org_id;
        if (!orgId) {
          toast.error('Organization ID is missing');
          return;
        }
        const isExpired = !form.is_paid && !form.is_active;
        if (form.is_paid !== undefined || isExpired) {
          payload.is_paid = form.is_paid;
          payload.is_active = form.is_active;
          if (form.is_paid) {
            payload.subscription_period_months = form.subscription_period_months;
            payload.subscription_period_days = form.subscription_period_days;
          }
        }
        const res = await updateOrganization(orgId, payload);
        
        toast.success('Organization updated successfully!');
        onSubmit(res);
      }
      onClose();
    } catch (err) {
      console.error('Failed to save organization:', err);
      const errorMsg =
        typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.response?.data?.detail ||
            err?.response?.data?.message ||
            err?.message ||
            'Failed to save organization';

      // Handle seat limit error
      if (
        errorMsg.includes('seat limit') ||
        errorMsg.includes('Available seats')
      ) {
        setErrors({
          total_seats: errorMsg,
        });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === 'edit' ? 'Edit Organization' : 'Create Organization';
  const cta = mode === 'edit' ? 'Save Changes' : 'Create Organization';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: (theme) => ({
          border: `1px solid #1B2640`,
          boxShadow: '0 6px 24px rgba(27,38,64,0.25)',
          backgroundColor: theme.palette.background.paper,
        }),
      }}
    >
      <DialogTitle sx={{ p: '16px 24px 8px' }}>
        <TitleWrap>
          <IconBadge>
            <BusinessRoundedIcon sx={{ color: '#3D9462', fontSize: 20 }} />
          </IconBadge>

          <Box sx={{ minWidth: 0 }}>
            <TitleText>{title}</TitleText>
            <TitleUnderline />
          </Box>
        </TitleWrap>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>

              <Grid item xs={12}>
                <StyledTextField
                  name="org_name"
                  label="Organization Name"
                  value={form.org_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 100) {
                      setForm((f) => ({ ...f, org_name: value }));
                      setErrors((prev) => ({ ...prev, org_name: '' }));
                    }
                  }}
                  variant="outlined"
                  fullWidth
                  required
                  error={!!errors.org_name}
                  helperText={
                    errors.org_name || `${form.org_name.length}/100 characters`
                  }
                  disabled={submitting}
                />
              </Grid>

              <Grid item xs={12}>
                <StyledTextField
                  name="description"
                  label="Description"
                  value={form.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 200) {
                      setForm((f) => ({ ...f, description: value }));
                      setErrors((prev) => ({ ...prev, description: '' }));
                    }
                  }}
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={3}
                  error={!!errors.description}
                  helperText={
                    errors.description ||
                    `${form.description.length}/200 characters`
                  }
                  disabled={submitting}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <StyledTextField
                  name="total_seats"
                  label="Total Seats"
                  type="number"
                  value={form.total_seats}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    if (value >= 0 || e.target.value === '') {
                      setForm((f) => ({
                        ...f,
                        total_seats: e.target.value === '' ? '' : value,
                      }));
                      setErrors((prev) => ({ ...prev, total_seats: '' }));
                    }
                  }}
                  variant="outlined"
                  fullWidth
                  required
                  error={!!errors.total_seats}
                  helperText={
                    errors.total_seats ||
                    (mode === 'edit' && usedSeats > 0
                      ? `Currently used: ${usedSeats} seats`
                      : '')
                  }
                  disabled={submitting}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>

              {mode === 'edit' && usedSeats > 0 && (
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: '#F8FAFF',
                      border: '1px solid #E5E7EB',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      <strong>Used Seats:</strong> {usedSeats} (read-only)
                    </Typography>
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.is_active}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, is_active: e.target.checked }))
                      }
                      disabled={submitting}
                      sx={{
                        color: '#1B2640',
                        '&.Mui-checked': {
                          color: '#3D9462',
                        },
                        '&.Mui-disabled': {
                          color: '#9e9e9e',
                        },
                      }}
                    />
                  }
                  label="Active Status"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#1B2640', fontWeight: 600 }}>
                  IP Whitelist (Optional)
                </Typography>
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.ip_whitelist_enabled}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, ip_whitelist_enabled: e.target.checked }))
                      }
                      disabled={submitting}
                      sx={{
                        color: '#1B2640',
                        '&.Mui-checked': {
                          color: '#3D9462',
                        },
                        '&.Mui-disabled': {
                          color: '#9e9e9e',
                        },
                      }}
                    />
                  }
                  label="Enable IP Whitelist"
                  sx={{ mb: 1.5 }}
                />
                
                {form.ip_whitelist_enabled && (
                  <>
                    <Typography variant="caption" sx={{ mb: 2, color: '#6b7280', display: 'block' }}>
                      Restrict access to specific IP addresses or ranges. Only requests from these IPs will be allowed.
                      <br />
                      Format: Single IP (e.g., 192.168.1.1) or CIDR range (e.g., 192.168.1.0/24)
                    </Typography>
                  </>
                )}
                
                {!form.ip_whitelist_enabled && (
                  <Typography variant="caption" sx={{ mb: 2, color: '#9ca3af', fontStyle: 'italic', display: 'block' }}>
                    IP whitelist is disabled. All IP addresses are allowed.
                  </Typography>
                )}
                
                {form.ip_whitelist_enabled && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="e.g., 192.168.1.0/24 or 10.0.0.1"
                        value={newIpRange}
                        onChange={(e) => {
                          setNewIpRange(e.target.value);
                          if (ipError) setIpError('');
                        }}
                        onBlur={() => {
                          if (newIpRange.trim()) {
                            const formatted = autoFormatIpAddress(newIpRange.trim());
                            setNewIpRange(formatted);
                          }
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const trimmed = newIpRange.trim();
                            if (trimmed) {
                              const validation = validateIpRange(trimmed);
                              if (!validation.valid) {
                                setIpError(validation.error);
                                toast.error(validation.error);
                                return;
                              }
                              setIpError('');
                              const ipRanges = form.allowed_ip_ranges || [];
                              if (!ipRanges.includes(validation.formatted)) {
                                setForm((f) => ({
                                  ...f,
                                  allowed_ip_ranges: [...ipRanges, validation.formatted],
                                }));
                                setNewIpRange('');
                              } else {
                                toast.warning('IP range already added');
                              }
                            }
                          }
                        }}
                        error={!!ipError}
                        helperText={ipError}
                        disabled={submitting}
                        sx={{ flexGrow: 1 }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon sx={{ color: '#3D9462' }} />}
                        onClick={() => {
                          const trimmed = newIpRange.trim();
                          if (trimmed) {
                            const validation = validateIpRange(trimmed);
                            if (!validation.valid) {
                              setIpError(validation.error);
                              toast.error(validation.error);
                              return;
                            }
                            setIpError('');
                            const ipRanges = form.allowed_ip_ranges || [];
                            if (!ipRanges.includes(validation.formatted)) {
                              setForm((f) => ({
                                ...f,
                                allowed_ip_ranges: [...ipRanges, validation.formatted],
                              }));
                              setNewIpRange('');
                            } else {
                              toast.warning('IP range already added');
                            }
                          }
                        }}
                        disabled={submitting || !newIpRange.trim()}
                        sx={{ borderColor: '#1B2640', color: '#1B2640' }}
                      >
                        Add
                      </Button>
                    </Box>
                    
                    {form.allowed_ip_ranges && form.allowed_ip_ranges.length > 0 && (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          bgcolor: '#F8FAFF',
                          borderColor: '#E5E7EB',
                          maxHeight: 150,
                          overflow: 'auto',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {form.allowed_ip_ranges.map((ipRange, index) => (
                            <Chip
                              key={index}
                              label={ipRange}
                              onDelete={() => {
                                const ipRanges = [...form.allowed_ip_ranges];
                                ipRanges.splice(index, 1);
                                setForm((f) => ({
                                  ...f,
                                  allowed_ip_ranges: ipRanges,
                                }));
                              }}
                              deleteIcon={<DeleteIcon sx={{ color: '#3D9462' }} />}
                              sx={{
                                bgcolor: '#EEF2FF',
                                color: '#1B2640',
                                '& .MuiChip-deleteIcon': {
                                  color: '#3D9462',
                                  '&:hover': { color: '#d32f2f' },
                                },
                              }}
                              disabled={submitting}
                            />
                          ))}
                        </Box>
                      </Paper>
                    )}
                    
                    {(!form.allowed_ip_ranges || form.allowed_ip_ranges.length === 0) && (
                      <Typography variant="caption" sx={{ color: '#ed6c02', fontStyle: 'italic' }}>
                        ⚠️ No IP ranges added. When enabled, all IPs will be blocked unless added here.
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#1B2640', fontWeight: 600 }}>
                  Trial Settings
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Subscription Status</InputLabel>
                      <Select
                        value={
                          form.is_paid ? 'paid' : 
                          (!form.is_active && !form.is_paid) ? 'expired' : 
                          'trial'
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === 'paid') {
                            setForm((f) => ({
                              ...f,
                              is_paid: true,
                              is_active: true,
                              subscription_period_months: f.subscription_period_months || 3,
                              subscription_period_days: null,
                            }));
                          } else if (value === 'expired') {
                            setForm((f) => ({
                              ...f,
                              is_paid: false,
                              is_active: false,
                              subscription_period_months: null,
                              subscription_period_days: null,
                              trial_end_date: null,
                            }));
                          } else {
                            const now = new Date();
                            const defaultTrialDays = form.trial_days || 15;
                            const trialEndDate = new Date(now.getTime() + defaultTrialDays * 24 * 60 * 60 * 1000);
                            setForm((f) => ({
                              ...f,
                              is_paid: false,
                              is_active: true,
                              subscription_period_months: null,
                              subscription_period_days: null,
                              trial_end_date: trialEndDate.toISOString(),
                              trial_start_date: now.toISOString(),
                            }));
                          }
                        }}
                        disabled={submitting}
                        label="Subscription Status"
                      >
                        <MenuItem value="trial">On Trial</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                        <MenuItem value="expired">Expired</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {form.is_paid && (
                    <>
                      {mode === 'edit' && subscriptionRemaining && subscriptionEndDate && (
                        <Grid item xs={12}>
                          <Chip
                            label={`Subscription: ${subscriptionRemaining}`}
                            color={
                              subscriptionRemaining === 'Expired' 
                                ? 'error' 
                                : (() => {
                                    const endDate = new Date(subscriptionEndDate);
                                    const now = new Date();
                                    const diffDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                                    return diffDays < 7 ? 'warning' : 'success';
                                  })()
                            }
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="caption" sx={{ display: 'block', color: '#666', mt: 0.5 }}>
                            Expires: {new Date(subscriptionEndDate).toLocaleDateString()}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>Subscription Period</InputLabel>
                          <Select
                            value={form.subscription_period_months ? `months_${form.subscription_period_months}` : form.subscription_period_days ? `days_${form.subscription_period_days}` : ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value.startsWith('months_')) {
                                const months = parseInt(value.replace('months_', ''));
                                setForm((f) => ({
                                  ...f,
                                  subscription_period_months: months,
                                  subscription_period_days: null,
                                }));
                              } else if (value.startsWith('days_')) {
                                const days = parseInt(value.replace('days_', ''));
                                setForm((f) => ({
                                  ...f,
                                  subscription_period_months: null,
                                  subscription_period_days: days,
                                }));
                              } else if (value === 'custom') {
                                setForm((f) => ({
                                  ...f,
                                  subscription_period_months: null,
                                  subscription_period_days: 30,
                                }));
                              }
                            }}
                            disabled={submitting}
                            label="Subscription Period"
                          >
                            <MenuItem value="months_1">1 Month</MenuItem>
                            <MenuItem value="months_3">3 Months</MenuItem>
                            <MenuItem value="months_6">6 Months</MenuItem>
                            <MenuItem value="months_12">12 Months</MenuItem>
                            <MenuItem value="custom">Custom (Days)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      {form.subscription_period_days !== null && (
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            name="subscription_period_days"
                            label="Custom Period (Days)"
                            type="number"
                            value={form.subscription_period_days || ''}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              if (value >= 1 || e.target.value === '') {
                                setForm((f) => ({
                                  ...f,
                                  subscription_period_days: e.target.value === '' ? null : value,
                                }));
                              }
                            }}
                            variant="outlined"
                            fullWidth
                            disabled={submitting}
                            InputProps={{
                              inputProps: { min: 1 },
                            }}
                          />
                        </Grid>
                      )}
                    </>
                  )}

                  {!form.is_paid && !form.is_active && (
                    <Grid item xs={12}>
                      <Chip
                        label="Subscription Expired"
                        color="error"
                        sx={{ mb: 1 }}
                      />
                    </Grid>
                  )}
                </Grid>

                {!form.is_paid && form.is_active && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          name="trial_days"
                          label="Trial Days"
                          type="number"
                          value={form.trial_days}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 1 || e.target.value === '') {
                              setForm((f) => ({
                                ...f,
                                trial_days: e.target.value === '' ? '' : value,
                              }));
                            }
                          }}
                          variant="outlined"
                          fullWidth
                          error={!!errors.trial_days}
                          helperText={errors.trial_days || 'Default trial period in days'}
                          disabled={submitting || savingTrialConfig}
                          InputProps={{
                            inputProps: { min: 1 },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <StyledTextField
                          name="max_connectors"
                          label="Max Connectors"
                          type="number"
                          value={form.max_connectors}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 1 || e.target.value === '') {
                              setForm((f) => ({
                                ...f,
                                max_connectors: e.target.value === '' ? '' : value,
                              }));
                            }
                          }}
                          variant="outlined"
                          fullWidth
                          error={!!errors.max_connectors}
                          helperText={errors.max_connectors || 'Maximum active connectors allowed'}
                          disabled={submitting || savingTrialConfig}
                          InputProps={{
                            inputProps: { min: 1 },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={form.days_limit_enabled}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  days_limit_enabled: e.target.checked,
                                }))
                              }
                              disabled={submitting || savingTrialConfig}
                              sx={{
                                color: '#1B2640',
                                '&.Mui-checked': {
                                  color: '#3D9462',
                                },
                                '&.Mui-disabled': {
                                  color: '#9e9e9e',
                                },
                              }}
                            />
                          }
                          label="Enable Days Limit"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={form.connectors_limit_enabled}
                              onChange={(e) =>
                                setForm((f) => ({
                                  ...f,
                                  connectors_limit_enabled: e.target.checked,
                                }))
                              }
                              disabled={submitting || savingTrialConfig}
                              sx={{
                                color: '#1B2640',
                                '&.Mui-checked': {
                                  color: '#3D9462',
                                },
                                '&.Mui-disabled': {
                                  color: '#9e9e9e',
                                },
                              }}
                            />
                          }
                          label="Enable Connectors Limit"
                        />
                      </Grid>
                    </Grid>

                    {mode === 'edit' && (
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleSaveTrialConfig}
                          disabled={savingTrialConfig || !hasTrialConfigChanges() || submitting}
                          sx={{
                            borderColor: '#1B2640',
                            color: '#1B2640',
                            '&:hover': {
                              borderColor: '#0F1720',
                              backgroundColor: 'rgba(27, 38, 64, 0.04)',
                            },
                            '&:disabled': {
                              borderColor: '#e0e0e0',
                              color: '#9e9e9e',
                            },
                          }}
                          startIcon={
                            savingTrialConfig ? (
                              <CircularProgress size={16} sx={{ color: '#1B2640' }} />
                            ) : null
                          }
                        >
                          {savingTrialConfig ? 'Saving...' : 'Save Trial Settings'}
                        </Button>
                      </Box>
                    )}

                    {mode === 'edit' && !form.is_paid && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: form.is_active ? '#F8FAFF' : '#FFF3E0', borderRadius: 1, border: `1px solid ${form.is_active ? '#E5E7EB' : '#FFB74D'}` }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                          {form.is_active ? 'Current Trial Status' : 'Reactivate Trial'}
                        </Typography>
                        {form.trial_end_date ? (
                          <>
                            <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mb: 1 }}>
                              Trial End Date: {new Date(form.trial_end_date).toLocaleString()}
                            </Typography>
                            {form.trial_start_date && (
                              <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mb: 2 }}>
                                Trial Start Date: {new Date(form.trial_start_date).toLocaleString()}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mb: 2 }}>
                            {form.is_active ? 'No trial period set' : 'This organization has expired. Start a new trial period to reactivate.'}
                          </Typography>
                        )}
                        
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <TextField
                            size="small"
                            type="number"
                            label={form.trial_end_date ? "Extend by (days)" : "Trial Period (days)"}
                            value={trialExtensionDays}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || (Number(val) >= 1 && Number(val) <= 365)) {
                                setTrialExtensionDays(val);
                              }
                            }}
                            sx={{ width: 150 }}
                            InputProps={{
                              inputProps: { min: 1, max: 365 },
                            }}
                            disabled={extendingTrial || submitting}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={async () => {
                              if (!trialExtensionDays || Number(trialExtensionDays) < 1) {
                                toast.error('Please enter a valid number of days (1-365)');
                                return;
                              }
                              
                              setExtendingTrial(true);
                              try {
                                await extendOrganizationTrial(organization.org_id, {
                                  extension_days: Number(trialExtensionDays),
                                });
                                toast.success(form.trial_end_date ? `Trial extended by ${trialExtensionDays} days` : `Trial started for ${trialExtensionDays} days`);
                                
                                const fresh = await getOrganizationById(organization.org_id);
                                setForm((f) => ({
                                  ...f,
                                  trial_end_date: fresh.trial_end_date || f.trial_end_date,
                                  is_active: fresh.is_active,
                                }));
                                if (originalValues && fresh.is_active !== originalValues.is_active) {
                                  setOriginalValues((prev) => ({
                                    ...prev,
                                    is_active: fresh.is_active,
                                  }));
                                }
                                setTrialExtensionDays('');
                                onSubmit(fresh);
                              } catch (err) {
                                console.error('Failed to extend trial:', err);
                                toast.error(
                                  err?.response?.data?.detail ||
                                    err?.response?.data?.message ||
                                    'Failed to extend trial'
                                );
                              } finally {
                                setExtendingTrial(false);
                              }
                            }}
                            disabled={extendingTrial || submitting || savingTrialConfig || !trialExtensionDays}
                            sx={{ borderColor: form.is_active ? '#1B2640' : '#FF9800', color: form.is_active ? '#1B2640' : '#FF9800' }}
                          >
                            {extendingTrial ? (form.trial_end_date ? 'Extending...' : 'Starting...') : (form.trial_end_date ? 'Extend Trial' : 'Start Trial')}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          size="small"
          sx={{ color: '#1B2640' }}
          disabled={submitting || loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={submitting || loading || (mode === 'edit' && !hasChangesForUpdate())}
          sx={{
            bgcolor: '#1B2640',
            '&:hover': { bgcolor: '#0F1720' },
          }}
          startIcon={
            submitting ? (
              <CircularProgress size={16} sx={{ color: '#fff' }} />
            ) : null
          }
        >
          {submitting ? 'Saving...' : cta}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

OrganizationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']),
  organization: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
